const CATALOG_API = process.env.REACT_APP_CATALOG_API_BASE_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Default images when primary_image_url is null */
const FALLBACK_IMAGES = {
  default_small: '/images/voltide-bess-under500.png',
  default_large: '/images/voltide-bess-over500.png',
};

/**
 * Normalize a dashboard catalog DTO to the format the frontend expects.
 * Maps dashboard field names → frontend field names.
 */
function normalizeProduct(p) {
  const kwh = p.battery_energy_kwh || p.battery_kwh || 0;
  const kw = p.rated_power_kw || p.pcs_kw || 0;
  const rentPerKwh = p.rent_from_inr_per_kwh_month;
  const buyPrice = p.buy_from_price_ex_gst;
  const image = p.primary_image_url || (kwh >= 500 ? FALLBACK_IMAGES.default_large : FALLBACK_IMAGES.default_small);

  return {
    // Identity
    sku: p.public_slug || p.slug || p.sku,
    slug: p.public_slug || p.slug || p.sku,
    voltide_sku_code: p.voltide_sku_code || '',
    product_name: p.public_product_name || p.product_name || '',
    sku_type: p.sku_type || 'SYSTEM',
    // Specs
    pcs_kw: kw,
    battery_kwh: kwh,
    form_factor: p.form_factor || '',
    cooling: p.cooling || '',
    use_case_tags: p.use_case_tags || [],
    feature_list: p.feature_list || [],
    // Images
    image,
    primary_image_url: p.primary_image_url,
    gallery_image_urls: p.gallery_image_urls || [],
    // Display controls
    featured: p.is_featured ?? p.featured ?? false,
    display_order: p.display_order ?? 999,
    install_complexity: p.install_complexity || 'STANDARD',
    checkout_eligible: p.checkout_eligible ?? false,
    // Pricing visibility (infer from price presence if not explicit)
    buy_price_visible: p.buy_price_visible ?? (buyPrice != null && buyPrice > 0),
    rent_price_visible: p.rent_price_visible ?? (rentPerKwh != null && rentPerKwh > 0),
    // Buy pricing
    buy_from_price_ex_gst: buyPrice || null,
    // Rental pricing
    rent_from_inr_per_kwh_month: rentPerKwh || null,
    rent_from_inr_per_month: rentPerKwh ? Math.round(kwh * rentPerKwh) : null,
    rent_assumptions_text: p.rent_assumptions_text || null,
    rent_min_disclaimer: p.rent_min_disclaimer || null,
    // Misc
    disclaimer_text: 'Prices are indicative and subject to change based on site conditions and final scope.',
    updated_at: p.updated_at || null,
    // Bundle
    bundle_components: p.bundle_components || null,
  };
}

/**
 * Fetch published products from dashboard catalog API.
 * Falls back to website backend in preview/dev.
 */
export async function fetchCatalogProducts() {
  const ts = Date.now();
  // Primary: dashboard catalog API
  if (CATALOG_API) {
    try {
      const res = await fetch(`${CATALOG_API}/api/catalog/website/products`);
      if (res.ok) {
        const data = await res.json();
        const products = data.map(normalizeProduct);
        return { products, source: CATALOG_API, fetchedAt: new Date(ts).toISOString(), fallback: false };
      }
      console.warn(`[catalog] API returned ${res.status}, falling back to direct DB`);
    } catch (err) {
      console.warn(`[catalog] API unreachable (${err.message}), falling back to direct DB`);
    }
  }
  // Fallback: direct website backend
  const res = await fetch(`${BACKEND_URL}/api/products`);
  const data = await res.json();
  const products = data.map(normalizeProduct);
  console.warn('[catalog] Using fallback: direct DB read via /api/products');
  return { products, source: `${BACKEND_URL} (fallback)`, fetchedAt: new Date(ts).toISOString(), fallback: true };
}

/**
 * Fetch single product by slug from dashboard catalog API.
 */
export async function fetchCatalogProduct(slug) {
  const ts = Date.now();
  if (CATALOG_API) {
    try {
      const res = await fetch(`${CATALOG_API}/api/catalog/website/products/${slug}`);
      if (res.ok) {
        const data = await res.json();
        return { product: normalizeProduct(data), source: CATALOG_API, fetchedAt: new Date(ts).toISOString(), fallback: false };
      }
      if (res.status === 404) {
        return { product: null, source: CATALOG_API, fetchedAt: new Date(ts).toISOString(), fallback: false };
      }
      console.warn(`[catalog] API returned ${res.status} for ${slug}, falling back`);
    } catch (err) {
      console.warn(`[catalog] API unreachable for ${slug}, falling back`);
    }
  }
  // Fallback
  const res = await fetch(`${BACKEND_URL}/api/products/${slug}`);
  const data = await res.json();
  if (data.error || data.detail) {
    return { product: null, source: `${BACKEND_URL} (fallback)`, fetchedAt: new Date(ts).toISOString(), fallback: true };
  }
  console.warn(`[catalog] Using fallback for ${slug}`);
  return { product: normalizeProduct(data), source: `${BACKEND_URL} (fallback)`, fetchedAt: new Date(ts).toISOString(), fallback: true };
}
